import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { KeyValue } from '@/main/models/common';
import {
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { TireBrandSaveRequest } from '@/main/models/request/tireBrandRequest';
import { getOptionsStatusActiveGeneral } from '@/main/services/data/data';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import * as S from './styles';
import { KOGAreaButtons } from '@/main/components/shared/styles';
import { TireBrandResponse } from '@/main/models/response/tireBrandResponse';
import { partBrandApi } from '@/main/api';
import { toast } from 'react-toastify';
import { StatusActiveGeneralEnum } from '@/main/services/enum/StatusActiveGeneralEnum';

const INITIAL_FORM_STATE_DEFAULT = {
  country: null as KeyValue | null,
  description: '',
  status: null as KeyValue | null
};
type IFormFiltersTireBrand = typeof INITIAL_FORM_STATE_DEFAULT;

interface IModalTireBrandProps {
  isOpenModal: boolean;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  itemTireBrand?: TireBrandResponse;
  refreshSearchGrid?: (guidTireBrand: String) => void;
  closeModal: () => void;
}

function ModalTireBrand({
  itemTireBrand,
  refreshSearchGrid = () => {},
  closeModal
}: IModalTireBrandProps) {
  const isEdit = !isNullUndefined(itemTireBrand);

  const dispatch = useDispatch();
  const methods = useForm<IFormFiltersTireBrand>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE_DEFAULT
  });
  const { control, handleSubmit, setValue } = methods;

  const { countriesUser: countryReducer } = useCountryReducer();
  const optionsStatus = getOptionsStatusActiveGeneral();

  const optionDefaultCountry = isEdit
    ? null
    : getDefaultOptionDrop(countryReducer.data);
  const optionDefaultStatus = isEdit
    ? null
    : optionsStatus.find(item => item.key === StatusActiveGeneralEnum.Active)!;
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, []);

  function loadEditTireBrand() {
    if (isNullUndefined(itemTireBrand)) return;

    const formattedStatus = itemTireBrand.active
      ? optionsStatus.find(item => item.key === StatusActiveGeneralEnum.Active)
      : optionsStatus.find(
          item => item.key == StatusActiveGeneralEnum.Inactive
        );
    const backendForFrontend: IFormFiltersTireBrand = {
      ...INITIAL_FORM_STATE_DEFAULT,
      country: {
        key: itemTireBrand.guidCountry,
        value: itemTireBrand.countryName
      },
      description: itemTireBrand?.description,
      status: formattedStatus as KeyValue
    };

    methods.reset(backendForFrontend);
  }

  function resetForm() {
    methods.reset();
    if (optionDefaultCountry) setValue('country', optionDefaultCountry);
    if (optionDefaultStatus) setValue('status', optionDefaultStatus);
  }

  async function onSubmitSave(data: IFormFiltersTireBrand) {
    setIsLoadingSave(true);
    const request: TireBrandSaveRequest = removeEmptyValues({
      ...(isEdit && { guid: itemTireBrand.guid }),
      guidCountry: data.country?.key as string,
      description: data.description,
      active: data.status?.key === StatusActiveGeneralEnum.Active
    });

    partBrandApi
      .saveTireBrand(request)
      .then(response => {
        toast.success(getTranslateByKey('information.savedSuccess'));

        refreshSearchGrid(String(response.data.result.guid));
        closeModal();
        resetForm();
      })
      .finally(() => {
        setIsLoadingSave(false);
      });
  }

  useEffect(() => {
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
  }, []);

  useEffect(() => {
    if (!isNullUndefined(itemTireBrand)) {
      loadEditTireBrand();
    }
  }, [JSON.stringify(itemTireBrand)]);

  const disabledCountry =
    isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;

  return (
    <form onSubmit={handleSubmit(onSubmitSave)} noValidate>
      <S.Container>
        <KogAutoCompleteControlled
          id="id-tireBrand-select-country-register"
          nameCustom="country"
          label={getTranslateByKey('information.country')}
          isDefaultValueUnique
          valueDefaultKey={optionDefaultCountry?.key}
          control={control}
          rulesCustom={{ required: true }}
          setValueCustom={setValue}
          options={countryReducer.data}
          isLoading={countryReducer.isLoading}
          disabled={isEdit || disabledCountry}
          onChangeCustom={() => {}}
        />

        <KogInputTextControlledNew
          id="id-tireBrand-txt-description"
          name="description"
          label={getTranslateByKey('information.tireBrand')}
          control={control}
          controllerProps={{ rules: { required: true, maxLength: 200 } }}
        />

        <KogAutoCompleteControlled
          id="id-tireBrand-select-status"
          nameCustom="status"
          label={getTranslateByKey('information.status')}
          valueDefaultKey={optionDefaultStatus?.key}
          control={control}
          rulesCustom={{ required: true }}
          setValueCustom={setValue}
          options={optionsStatus}
          onChangeCustom={() => {}}
        />

        <KOGAreaButtons style={{ justifyContent: 'center' }}>
          <SecondaryButton
            id="id-tireBrand-btn-save"
            type="submit"
            isLoading={isLoadingSave}>
            {getTranslateByKey('information.save')}
          </SecondaryButton>
        </KOGAreaButtons>
      </S.Container>
    </form>
  );
}

function WrapperModalTireBrand(props: IModalTireBrandProps) {
  const isEdit = !isNullUndefined(props.itemTireBrand);
  const titleSaveModal = getTranslateByKey('information.registerTireBrand');
  const titleEditModal = getTranslateByKey('information.editTireBrand');
  const titleModal = isEdit ? titleEditModal : titleSaveModal;

  if (!props.isOpenModal) return null;

  return (
    <KogModalSideComponent
      open={props.isOpenModal}
      shouldRenderOpen
      title={titleModal}
      closeModal={() => props.setIsOpenModal(false)}>
      <ModalTireBrand {...props} />
    </KogModalSideComponent>
  );
}

export default WrapperModalTireBrand;
